import { useMemo, useState } from "react";
import { FiGlobe, FiTarget, FiLink, FiMapPin, FiSearch, FiLayers } from "react-icons/fi";

const iconMap = {
    "Traffic Sources": <FiGlobe size={16} />,
    "Campaigns": <FiTarget size={16} />,
    "Landing Pages": <FiMapPin size={16} />,
    "Top Referrers": <FiLink size={16} />,
    "UTM Mediums": <FiLayers size={16} />,
    "UTM Terms": <FiSearch size={16} />,
    "Campaign Performance": <FiTarget size={16} />,
    "Visitor Entry Pages": <FiMapPin size={16} />,
    "Traffic Acquisition Channels": <FiLayers size={16} />,
    "External Referrals": <FiLink size={16} />,
};

const accentStyles = {
    indigo: { chip: "bg-indigo-100 text-indigo-600", bar: "from-indigo-500 to-violet-500", panel: "from-indigo-50/60" },
    violet: { chip: "bg-violet-100 text-violet-600", bar: "from-violet-500 to-purple-500", panel: "from-violet-50/60" },
    sky: { chip: "bg-sky-100 text-sky-600", bar: "from-sky-500 to-blue-500", panel: "from-sky-50/60" },
    amber: { chip: "bg-amber-100 text-amber-600", bar: "from-amber-500 to-orange-500", panel: "from-amber-50/60" },
    rose: { chip: "bg-rose-100 text-rose-600", bar: "from-rose-500 to-pink-500", panel: "from-rose-50/60" },
};


const compact = new Intl.NumberFormat("en", { notation: "compact" });

const AnalyticsRankingCard = ({
    title,
    subtitle,
    data = [],
    labelKey,
    valueKey,
    accent = "indigo",
    limit = 6,
}) => {
    const [expanded, setExpanded] = useState(false);
    const s = accentStyles[accent] || accentStyles.indigo;
    const sorted = useMemo(
        () => [...data].sort((a, b) => (b[valueKey] || 0) - (a[valueKey] || 0)),
        [data, valueKey]
    );
    const remaining = Math.max(sorted.length - limit, 0);
    const visible = expanded ? sorted : sorted.slice(0, limit);
    const maxValue = visible[0]?.[valueKey] || 1;

    return (
        <div className={`rounded-xl border border-slate-200 bg-gradient-to-br ${s.panel} to-white`}>
            <div className="flex items-center justify-between px-4 py-3">
                <div className="flex items-start gap-2.5">
                    <div className={`mt-0.5 flex h-8 w-8 items-center justify-center rounded-lg ${s.chip}`}>
                        {iconMap[title]}
                    </div>
                    <div>
                        <h3 className="text-sm font-semibold text-slate-900">
                            {title}
                        </h3>
                        {subtitle && (
                            <p className="text-xs text-slate-500">
                                {subtitle}
                            </p>
                        )}
                    </div>
                </div>

                {sorted.length > 0 && (
                    <span className="text-[11px] font-medium text-slate-400">
                        {sorted.length} total
                    </span>
                )}
            </div>

            <div className="space-y-3 px-4 pb-4">
                {visible.length === 0 && (
                    <p className="py-4 text-center text-xs text-slate-400">No data yet</p>
                )}

                {visible.map((item, i) => {
                    const percent = ((item[valueKey] || 0) / maxValue) * 100;
                    return (
                        <div key={item[labelKey] || i} className="flex items-center gap-2.5">
                            <span className="w-4 shrink-0 text-[11px] font-semibold text-slate-400">
                                {i + 1}
                            </span>

                            <div className="min-w-0 flex-1">
                                <div className="mb-1 flex items-center justify-between gap-2 text-xs">
                                    <span
                                        className="truncate font-medium text-slate-700"
                                        title={item[labelKey]}
                                    >
                                        {
                                            labelKey === "page"
                                                ? (
                                                    item.page === "/"
                                                        ? "Home Page"
                                                        : item.page
                                                )
                                                : (
                                                    item[labelKey] || "Unknown"
                                                )
                                        }
                                    </span>
                                    <span className="shrink-0 font-semibold text-slate-900">
                                        {compact.format(item[valueKey] || 0)}
                                    </span>
                                </div>

                                <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
                                    <div
                                        className={`h-full rounded-full bg-gradient-to-r ${s.bar}`}
                                        style={{ width: `${percent}%` }}
                                    />
                                </div>
                            </div>
                        </div>
                    );
                })}

                {(remaining > 0 || expanded) && (
                    <button
                        type="button"
                        onClick={() => setExpanded(!expanded)}
                        className="w-full pt-2 text-center text-[11px] font-medium text-indigo-600 hover:text-indigo-700"
                    >
                        {expanded ? "Show Less" : `+${remaining} more`}
                    </button>
                )}
            </div>
        </div>
    );
};

export default AnalyticsRankingCard;